import TournamentOrganizer from "tournament-organizer";
import { TOURNAMENT_DEFAULT_SCORING, TOURNAMENT_GAME_RESULT, } from "../../constants";
export class TournamentOrganizerFacade {
    organizer;
    tourney = null;
    constructor() {
        this.organizer = new TournamentOrganizer();
    }
    initialize(tournament, games, players) {
        const options = this.createTournamentOptions(tournament, games, players);
        this.tourney = this.organizer.reloadTournament(options);
    }
    getStandings() {
        if (!this.tourney)
            throw new Error("Tournament not initialized");
        return this.tourney.standings(false).map((s) => ({
            gamePoints: s.gamePoints,
            games: s.games,
            matchPoints: s.matchPoints,
            matches: s.matches,
            player: {
                id: s.player.id,
                name: s.player.name,
                active: s.player.active,
                value: s.player.value || 0,
                // eslint-disable-next-line
                matches: s.player.matches.map(({ id, ...rest }) => rest),
                meta: s.player.meta,
            },
            wins: s.matchPoints,
            tiebreaker: s.tiebreaks.medianBuchholz * 100000000 +
                s.tiebreaks.solkoff * 10000 +
                s.tiebreaks.cumulative,
        }));
    }
    getPairings(roundNumber) {
        if (!this.tourney)
            throw new Error("Tournament not initialized");
        this.tourney.start();
        return this.tourney.matches
            .filter((m) => m.round === roundNumber)
            .map((matchup) => ({
            whiteUsername: matchup.player1 ? matchup.player1.id : null,
            blackUsername: matchup.player2 ? matchup.player2.id : null,
        }));
    }
    enterElimResults(games) {
        if (!this.tourney)
            throw new Error("Tournament not initialized");
        for (const match of this.tourney.matches) {
            const game = games.find((g) => g.roundNumber === match.round &&
                g.board === match.match &&
                (g.whiteUsername || g.blackUsername));
            if (!game) {
                return;
            }
            // we don't need to reconstruct result of a pairing bye game
            const ignoreResultValues = [
                TOURNAMENT_GAME_RESULT.PAIRINGBYE,
                TOURNAMENT_GAME_RESULT.FULLBYE,
                TOURNAMENT_GAME_RESULT.HALFBYE,
                TOURNAMENT_GAME_RESULT.ZEROBYE,
                TOURNAMENT_GAME_RESULT.DISQUALIFIED,
            ];
            if (ignoreResultValues.includes(game.whiteResult) ||
                ignoreResultValues.includes(game.blackResult)) {
                return;
            }
            else {
                let player1Wins = 0;
                let player2Wins = 0;
                let draws = 0;
                if (game.whiteUsername === match.player1.id) {
                    if (game.whiteResult === TOURNAMENT_GAME_RESULT.WIN ||
                        game.whiteResult === TOURNAMENT_GAME_RESULT.FORFEITW) {
                        player1Wins = 1;
                    }
                    if (game.blackResult === TOURNAMENT_GAME_RESULT.WIN ||
                        game.blackResult === TOURNAMENT_GAME_RESULT.FORFEITW) {
                        player2Wins = 1;
                    }
                    if (game.whiteResult === TOURNAMENT_GAME_RESULT.DRAW ||
                        game.whiteResult === TOURNAMENT_GAME_RESULT.FORFEITD) {
                        draws = 1;
                    }
                }
                else {
                    if (game.blackResult === TOURNAMENT_GAME_RESULT.WIN ||
                        game.blackResult === TOURNAMENT_GAME_RESULT.FORFEITW) {
                        player1Wins = 1;
                    }
                    else if (game.blackResult === TOURNAMENT_GAME_RESULT.LOSS ||
                        game.blackResult === TOURNAMENT_GAME_RESULT.FORFEITL) {
                        player2Wins = 1;
                    }
                    else if (game.blackResult === TOURNAMENT_GAME_RESULT.DRAW ||
                        game.blackResult === TOURNAMENT_GAME_RESULT.FORFEITD) {
                        draws = 1;
                    }
                }
                this.tourney.enterResult(match.id, player1Wins, player2Wins, draws);
            }
        }
    }
    createTournamentOptions(tournament, games, players) {
        const defaultOptions = {
            id: tournament.id,
            name: tournament.name,
            seating: true,
            status: "stage-one",
            round: 1,
            sorting: "ascending",
            scoring: TOURNAMENT_DEFAULT_SCORING,
            meta: {},
            stageOne: {
                format: "single-elimination",
                consolation: false,
                rounds: tournament.totalRounds,
                initialRound: 1,
                maxPlayers: players.length,
            },
            stageTwo: {
                format: null, // no stage two
                consolation: false,
                advance: {
                    value: 0,
                    method: "all",
                },
            },
            matches: this.toTourneyMatches(games),
            players: this.toTourneyPlayers(tournament, players, games),
        };
        switch (tournament.type) {
            case "Swiss": {
                defaultOptions.stageOne.format = "swiss";
                break;
            }
            case "Robin": {
                defaultOptions.stageOne.format = "round-robin";
                break;
            }
            case "Elim": {
                defaultOptions.stageOne.format = "single-elimination";
                break;
            }
        }
        return defaultOptions;
    }
    toTourneyMatches = (games) => {
        return games
            .filter((g) => g.whiteUsername || g.blackUsername)
            .map((g) => {
            const whiteRoundGames = games.filter((g1) => g1.roundNumber === g.roundNumber &&
                g1.whiteUsername === g.whiteUsername);
            const blackRoundGames = games.filter((g1) => g1.roundNumber === g.roundNumber &&
                g1.whiteUsername === g.blackUsername);
            // a match can have multiple games, hence count win, loss, draw
            const values = {
                id: g.matchGUID,
                active: false,
                bye: g.whiteResult === TOURNAMENT_GAME_RESULT.PAIRINGBYE ||
                    g.blackResult === TOURNAMENT_GAME_RESULT.PAIRINGBYE,
                player1: {
                    id: g.whiteUsername,
                    win: whiteRoundGames.reduce((win, g1) => {
                        return (win +
                            (g1.whiteUsername === g.whiteUsername
                                ? g1.whiteResult === TOURNAMENT_GAME_RESULT.WIN ||
                                    g1.whiteResult === TOURNAMENT_GAME_RESULT.FORFEITW ||
                                    g1.whiteResult === TOURNAMENT_GAME_RESULT.PAIRINGBYE
                                    ? 1
                                    : 0
                                : g1.blackResult === TOURNAMENT_GAME_RESULT.WIN ||
                                    g1.blackResult === TOURNAMENT_GAME_RESULT.FORFEITW ||
                                    g1.blackResult === TOURNAMENT_GAME_RESULT.PAIRINGBYE
                                    ? 1
                                    : 0));
                    }, 0),
                    loss: whiteRoundGames.reduce((loss, g1) => {
                        return (loss +
                            (g1.whiteUsername === g.whiteUsername
                                ? g1.whiteResult === TOURNAMENT_GAME_RESULT.LOSS ||
                                    g1.whiteResult === TOURNAMENT_GAME_RESULT.FORFEITL
                                    ? 1
                                    : 0
                                : g1.blackResult === TOURNAMENT_GAME_RESULT.LOSS ||
                                    g1.blackResult === TOURNAMENT_GAME_RESULT.FORFEITL
                                    ? 1
                                    : 0));
                    }, 0),
                    draw: whiteRoundGames.reduce((draw, g1) => {
                        return (draw +
                            (g1.whiteUsername === g.whiteUsername
                                ? g1.whiteResult === TOURNAMENT_GAME_RESULT.DRAW ||
                                    g1.whiteResult === TOURNAMENT_GAME_RESULT.FORFEITD
                                    ? 1
                                    : 0
                                : g1.blackResult === TOURNAMENT_GAME_RESULT.DRAW ||
                                    g1.blackResult === TOURNAMENT_GAME_RESULT.FORFEITD
                                    ? 1
                                    : 0));
                    }, 0),
                },
                player2: {
                    id: g.blackUsername,
                    win: blackRoundGames.reduce((win, g1) => {
                        return (win +
                            (g1.whiteUsername === g.whiteUsername
                                ? g1.whiteResult === TOURNAMENT_GAME_RESULT.WIN ||
                                    g1.whiteResult === TOURNAMENT_GAME_RESULT.FORFEITW ||
                                    g1.whiteResult === TOURNAMENT_GAME_RESULT.PAIRINGBYE
                                    ? 1
                                    : 0
                                : g1.blackResult === TOURNAMENT_GAME_RESULT.WIN ||
                                    g1.blackResult === TOURNAMENT_GAME_RESULT.FORFEITW ||
                                    g1.blackResult === TOURNAMENT_GAME_RESULT.PAIRINGBYE
                                    ? 1
                                    : 0));
                    }, 0),
                    loss: blackRoundGames.reduce((loss, g1) => {
                        return (loss +
                            (g1.whiteUsername === g.whiteUsername
                                ? g1.whiteResult === TOURNAMENT_GAME_RESULT.LOSS ||
                                    g1.whiteResult === TOURNAMENT_GAME_RESULT.FORFEITL
                                    ? 1
                                    : 0
                                : g1.blackResult === TOURNAMENT_GAME_RESULT.LOSS ||
                                    g1.blackResult === TOURNAMENT_GAME_RESULT.FORFEITL
                                    ? 1
                                    : 0));
                    }, 0),
                    draw: blackRoundGames.reduce((draw, g1) => {
                        return (draw +
                            (g1.whiteUsername === g.whiteUsername
                                ? g1.whiteResult === TOURNAMENT_GAME_RESULT.DRAW ||
                                    g1.whiteResult === TOURNAMENT_GAME_RESULT.FORFEITD
                                    ? 1
                                    : 0
                                : g1.blackResult === TOURNAMENT_GAME_RESULT.DRAW ||
                                    g1.blackResult === TOURNAMENT_GAME_RESULT.FORFEITD
                                    ? 1
                                    : 0));
                    }, 0),
                },
                match: g.board,
                round: g.roundNumber,
                path: null,
                meta: null,
            };
            return values;
        });
    };
    toTourneyPlayers = (tournament, players, games = []) => {
        if (tournament.type === "RoundRobin") {
            players.sort((p1, p2) => p1.startingRank - p2.startingRank);
        }
        const playerValues = players.map((p) => {
            // get all the games player've played
            const playerGames = games.filter((m) => m.blackUsername === p.username || m.whiteUsername === p.username);
            // a match can have multiple games, but the system now can only support 1 game per match
            // if it's multiple games per a single match (best of x), then need to update to handle that case
            const values = {
                id: p.username,
                name: p.username,
                active: tournament.type === "Elim" ? true : p.active,
                value: p.seed,
                meta: {},
                matches: playerGames.map((g) => {
                    // a match can have multi games, but the system now can only support 1 game per match,
                    // so get opponent base on the first game for now,
                    const opponent = g.whiteUsername === p.username ? g.blackUsername : g.whiteUsername;
                    const color = g.whiteUsername === p.username ? "w" : "b";
                    const result = g.whiteUsername === p.username ? g.whiteResult : g.blackResult;
                    const win = result === TOURNAMENT_GAME_RESULT.WIN ||
                        result === TOURNAMENT_GAME_RESULT.FORFEITW ||
                        result === TOURNAMENT_GAME_RESULT.PAIRINGBYE
                        ? 1
                        : 0;
                    const loss = result === TOURNAMENT_GAME_RESULT.LOSS ||
                        result === TOURNAMENT_GAME_RESULT.FORFEITL
                        ? 1
                        : 0;
                    const draw = result === TOURNAMENT_GAME_RESULT.DRAW ||
                        result === TOURNAMENT_GAME_RESULT.FORFEITD
                        ? 1
                        : 0;
                    const bye = g.whiteResult === TOURNAMENT_GAME_RESULT.PAIRINGBYE ||
                        g.blackResult === TOURNAMENT_GAME_RESULT.PAIRINGBYE; // 10 = PAIRING_BYE
                    return {
                        id: g.matchGUID,
                        pairUpDown: false,
                        opponent,
                        seating: color === "w" ? 1 : -1,
                        bye,
                        // a match can have multiple games, hence count win, loss, draw in a particular match
                        win,
                        loss,
                        draw,
                        round: g.roundNumber,
                    };
                }),
            };
            return values;
        });
        return playerValues;
    };
}
